import React, { useState, useEffect, useContext, useCallback } from "react";
import API_BASE_URL from "../config/api";
import { UserOrdersContext } from '../context/UserOrdersContext';
import { publish } from '../utils/eventBus';
import GoogleLoginButton from "../components/GoogleLoginButton";


const LoginForm = ({ navigate, onLogin }) => {
  const { setOrders, setLoading: setOrdersLoading, setError: setOrdersError } = useContext(UserOrdersContext);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showResend, setShowResend] = useState(false);

  const prewarmApi = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2000);
      await fetch(`${API_BASE_URL}/api/health`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeout);
    } catch (e) {
      // ignore warmup errors
    }
  }, []);

  const fetchOrdersForToken = useCallback(async (token) => {
    setOrdersLoading(true);
    setOrdersError("");

    try {
      const ordersRes = await fetch(`${API_BASE_URL}/api/v1/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const ordersData = await ordersRes.json();
      if (ordersRes.ok && ordersData.success && Array.isArray(ordersData.orders)) {
        setOrders(ordersData.orders);
      } else {
        setOrders([]);
        setOrdersError(ordersData.message || 'Could not fetch orders');
      }
    } catch (err) {
      setOrders([]);
      setOrdersError('Could not fetch orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [setOrders, setOrdersError, setOrdersLoading]);

  const completeLogin = useCallback(async (data, emailOverride) => {
    localStorage.setItem("authToken", data.token);
    if (data.user?.username) localStorage.setItem('userName', data.user.username);
    if (data.user?.role) localStorage.setItem('userRole', data.user.role);

    await fetchOrdersForToken(data.token);

    const resolvedEmail = emailOverride || data.user?.email || email;
    onLogin(resolvedEmail, data.user?.username);
    try { publish('auth:login', { email: resolvedEmail, username: data.user?.username }); } catch (e) { /* ignore */ }

    setMessage("Login successful. Redirecting...");

    setTimeout(() => {
      navigate("products");
    }, 500);
  }, [email, fetchOrdersForToken, navigate, onLogin]);

  // -------------------------
  // Login
  // -------------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!email || !password) {
      setMessage("Email and password are required");
      return;
    }

    setLoading(true);

    try {
      await prewarmApi();
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 403) {
          setMessage(
            "Email not verified. Please verify using the OTP sent to your email."
          );
          localStorage.setItem("pendingVerifyEmail", email);
          setShowResend(true);
        } else {
          setMessage(data.message || "Login failed");
        }
        setLoading(false);
        return;
      }


      await completeLogin(data, email);

    } catch (err) {
      console.error("Login error:", err);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setMessage("");
    setLoading(true);

    try {
      await prewarmApi();
      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ credential }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(data.message || "Google login failed");
        return;
      }

      await completeLogin(data, data.user?.email);
    } catch (err) {
      console.error("Google login error:", err);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [completeLogin, prewarmApi]);

  useEffect(() => {
    // If there's a pending verify email saved (e.g. user landed here after signup), show resend option
    if (localStorage.getItem('pendingVerifyEmail')) {
      setShowResend(true);
    }
  }, []);

  // -------------------------
  // Forgot Password
  // -------------------------
  const handleForgotPassword = async () => {
    if (!email) {
      setMessage("Enter your email first");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setMessage(data.message || "Failed to send reset email");
        setLoading(false);
        return;
      }

      setMessage("Password reset OTP sent to your email");

      // Take user to reset password screen so they can enter the OTP
      navigate("resetPassword");
    } catch (err) {
      console.error("Forgot password error:", err);
      setMessage("Unable to process request. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Resend OTP
  // -------------------------
  const handleResendOtp = async () => {
    if (!email) {
      setMessage("Enter your email first");
      return;
    }

    setResending(true);
    setMessage("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setMessage(data.message || "Failed to resend OTP");
        setResending(false);
        return;
      }

      localStorage.setItem("pendingVerifyEmail", email);
      setMessage("OTP resent successfully. Check your email.");

      setTimeout(() => {
        navigate("/verify-otp");
      }, 600);

    } catch (err) {
      console.error("Resend OTP error:", err);
      setMessage("Unable to resend OTP. Try again later.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h2 className="text-3xl font-bold text-center mb-6">Login</h2>

      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto">
        <div className="space-y-3">
          <GoogleLoginButton
            clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            onCredential={handleGoogleCredential}
            onError={(err) => setMessage(err)}
            text="continue_with"
          />

          <div className="text-center text-gray-400 text-sm">──────── OR ────────</div>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div className="mt-4 flex flex-col gap-2 text-sm text-center">
          <button
            onClick={handleForgotPassword}
            disabled={loading}
            className="text-blue-600 hover:underline disabled:opacity-60"
          >
            Forgot password?
          </button>

          {showResend && (
            <button
              onClick={handleResendOtp}
              disabled={resending}
              className="text-blue-600 hover:underline disabled:opacity-60"
            >
              {resending ? "Resending OTP..." : "Resend verification OTP"}
            </button>
          )}

          <button
            onClick={() => navigate("/register")}
            className="text-blue-600 hover:underline"
          >
            Don’t have an account? Register
          </button>
        </div>

        {message && (
          <p className="mt-4 text-center text-sm text-red-600">{message}</p>
        )}
      </div>
    </div>
  );
};

export default LoginForm;

